import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ListPageLayout from '../components/hand/ListPageLayout';
import WallEventList from '../components/wall/WallEventList';
import useMyPortalSources from '../hooks/useMyPortalSources';
import EventViewSwitch from '../components/EventViewSwitch';
import useEventViewMode from '../hooks/useEventViewMode';
import BackToTop from '../components/BackToTop';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { featuredWallService } from '../services';

/**
 * /events/all — the shared "see all" list page.
 * docs/plans/2026-08-handoff-layout/03-see-all.md
 *
 * Every SEE ALL / VIEW ALL link in the app lands here. `?view=featured` shows
 * the curated picks; the default shows every live event near you, paged by
 * infinite scroll. Search results live at /search (a separate route, owner rule
 * DECISIONS D-2) and reuse this page's layout.
 *
 * The body is the events wall's own: WallEventCards in masonry columns (WallEventList), with the
 * OPEN A PORTAL ring and the PORTAL OPEN mark, so a SEE ALL doesn't land on a different card.
 */
const STORAGE_KEY = 'pr.userLocation';
const PAGE = 24;

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'free', label: 'Free' },
  { key: 'week', label: 'This week' },
  { key: 'starters', label: 'Party starters' },
];

function readLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** The next seven local dates (yyyy-MM-dd) — the THIS WEEK chip. */
function weekDays() {
  const out = [];
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  return out;
}

export default function EventsAllPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'featured' ? 'featured' : 'events';
  const filter = searchParams.get('filter') || 'all';
  const category = searchParams.get('category') || '';
  // Calendar's SEE ALL FOR THIS DAY lands here with ?day=yyyy-MM-dd.
  const day = searchParams.get('day') || '';
  // Search's "See all events in <city>" lands here with ?city=Kelowna.
  const city = (searchParams.get('city') || '').trim();
  const location = useMemo(readLocation, []);
  // GRID / LIST, shared with the featured wall's ALL EVENTS section and
  // persisted per user. Presentation only — switching never refetches.
  const [viewMode, setViewMode] = useEventViewMode('grid');
  const portalFor = useMyPortalSources();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const setFilter = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'all') next.delete('filter'); else next.set('filter', key);
      return next;
    });
  };

  const fetchPage = useCallback(async (skipVal) => {
    if (loading) return;
    setLoading(true);

    if (view === 'featured') {
      // Featured is a short curated set (max 10) — one request, no paging.
      const r = await featuredWallService.getWall({ lat: location?.lat, lng: location?.lng });
      const featured = (r.success && r.data?.featured) || [];
      setItems(featured.map((e) => ({ ...e, isFeatured: true })));
      setTotal(featured.length);
      setHasMore(false);
    } else {
      const r = await featuredWallService.getEvents({
        lat: location?.lat ?? null,
        lng: location?.lng ?? null,
        fundingType: filter === 'starters' ? 'party_starter' : null,
        category: category || null,
        cities: city ? [city] : null,
        days: day ? [day] : (filter === 'week' ? weekDays() : null),
        free: filter === 'free',
        skip: skipVal,
        take: PAGE,
      });
      if (r.success && r.data) {
        const batch = r.data.events || [];
        setItems((prev) => (skipVal === 0 ? batch : [...prev, ...batch]));
        setSkip(skipVal + batch.length);
        setHasMore(!!r.data.hasMore);
        setTotal((prev) => (r.data.hasMore ? null : (skipVal === 0 ? batch.length : skipVal + batch.length)) ?? prev);
      }
    }

    setLoading(false);
    setFirstLoad(false);
  }, [view, filter, category, day, city, location, loading]);

  // Reload from page 0 whenever the view, filter, or CITY changes. `city` must be
  // here: picking a new city in the search bar updates the URL's ?city= param, and
  // without it the title flips to the new city but the events stay on the old one.
  useEffect(() => {
    setItems([]);
    setSkip(0);
    setHasMore(true);
    setTotal(null);
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, filter, category, day, city]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) fetchPage(skip);
  }, [loading, hasMore, skip, fetchPage]);

  const sentinelRef = useInfiniteScroll(loadMore, { hasMore, loading });

  // When a city was asked for by name, THAT is where you are — not the location
  // saved on the account. Showing "ALL EVENTS · BARRIE" above a page of Toronto
  // events reads as having landed on the wrong city.
  // Never the browser's placeholder ("Your location") — that isn't a place.
  const savedLabel = location?.label && !/location|near me|nearby|current/i.test(location.label) ? location.label : '';
  const place = (city || savedLabel).toUpperCase();
  const dayLabel = day
    ? new Date(`${day}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase()
    : '';
  const eyebrow = view === 'featured'
    ? ['Featured', place].filter(Boolean).join(' · ')
    : [dayLabel || 'All events', place].filter(Boolean).join(' · ');

  const shownLabel = items.length === 0 ? '' : (
    total != null ? `Showing ${items.length} of ${total}` : `Showing ${items.length}`
  );

  const filterRow = (
    <>
      {view !== 'featured' && FILTERS.map((f) => (
        <button
          key={f.key}
          type="button"
          onClick={() => setFilter(f.key)}
          className={`flex h-[38px] flex-shrink-0 items-center border-2 border-black px-3.5 font-mono text-[12px] uppercase tracking-[0.04em] transition-colors ${
            filter === f.key ? 'bg-black text-white' : 'bg-white text-black hover:bg-black hover:text-white'
          }`}
        >
          {f.label}
        </button>
      ))}
      {/* The switch is pushed right; `shownLabel` trails it in the same strip. */}
      <EventViewSwitch value={viewMode} onChange={setViewMode} className="ml-auto" />
    </>
  );

  return (
    <>
    <ListPageLayout
      eyebrow={eyebrow}
      title={view === 'featured' ? 'All featured' : city ? `All events in ${city}` : 'All events'}
      subtitle={view === 'featured'
        ? "Everything we're putting in front of people near you."
        : city ? `Everything live in ${city} right now.`
          : 'Everything live near you right now.'}
      active="events"
      filters={filterRow}
      shownLabel={shownLabel}
      sentinelRef={sentinelRef}
      loading={loading}
      hasMore={hasMore}
      isEmpty={!loading && !firstLoad && items.length === 0}
      layout="bare"
      empty={(
        <p className="py-12 text-center font-mono text-[12px] uppercase tracking-[0.14em] text-hand-muted">
          {filter === 'all' ? 'Nothing live nearby yet.' : 'No events match this filter.'}
        </p>
      )}
    >
      {/* First load / city / filter change → a full skeleton page; the next page loading → a
          few skeleton cards under the list. */}
      <WallEventList
        events={items}
        viewMode={viewMode}
        resetKey={`${view}|${filter}|${category}|${day}|${city}`}
        refreshing={loading && items.length === 0}
        fetching={loading && items.length > 0}
        portalFor={portalFor}
      />
    </ListPageLayout>
    <BackToTop />
    </>
  );
}
